import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LocalLibraryRoundedIcon from '@mui/icons-material/LocalLibraryRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import CoursesApi from '../../../apis/CoursesApi';
import { CourseHomePageDto } from '../../../generated-types/courseHomePageDto';
import useApi from '../../../lib/useApi';
import { SectionTitleFontSize } from '../HomeView';

/**
 * A sections on the home page to describe what S3G does
 */
const WhatWeDoSection = () => {
  const coursesApi = useApi(CoursesApi);
  const [courseInfo, setCourseInfo] = useState<CourseHomePageDto[]>();
  const [cardMapping, setCardMapping] = useState<Record<string, string>>({});
  const [isFlippedMap, setIsFlippedMap] = useState<Record<string, boolean>>({});
  const cardHeight = '400px';
  const cardElevation = 10;

  /** This use effect loads the course information for the cards */
  useEffect(() => {
    const init = async () => {
      try {
        // Load the courses
        const courses = await coursesApi.GetCoursesForHomePage();

        // Setup the cards
        const mapping: Record<string, string> = {};
        const flipMap: Record<string, boolean> = {};
        for (const course of courses) {
          const cardBlob = await coursesApi.GetCourseCard(course.slug);
          const cardUrl = window.URL.createObjectURL(cardBlob);
          mapping[course.slug] = cardUrl;
          flipMap[course.slug] = false;
        }

        // Set the state
        setCourseInfo(courses);
        setIsFlippedMap(flipMap);
        setCardMapping(mapping);
      } catch {
        // Do nothing
      }
    };
    init();
  }, [coursesApi]);

  /**
   * Flip the specified card
   * @param slug The course slug for the card. Indicates which card to flip
   */
  const flipCard = useCallback((slug: string) => {
    setIsFlippedMap((f) => {
      f[slug] = !f[slug];
      return { ...f };
    });
  }, []);

  /**
   * Get the icon for the course. If the icon is missing, it defaults to a school icon.
   * @param slug The course slug for the card. Indicates which icon to use.
   */
  const getIconForCourse = useCallback((slug: string) => {
    const iconSize = '5em';
    switch (slug) {
      case 'active-shooter-response-training':
        return (
          <VerifiedUserRoundedIcon color="error" sx={{ fontSize: iconSize }} />
        );
      case 'art-of-de-escalation':
        return <ForumRoundedIcon color="warning" sx={{ fontSize: iconSize }} />;
      case 'mastering-emotional-intelligence-conflict-resilience':
        return (
          <LocalLibraryRoundedIcon
            color="primary"
            sx={{ fontSize: iconSize }}
          />
        );
      default:
        return <SchoolRoundedIcon />;
    }
  }, []);

  return (
    <Box sx={{ py: 8, backgroundColor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          fontWeight={700}
          gutterBottom
          textAlign="center"
          fontSize={SectionTitleFontSize}
        >
          A Practical, Proven Approach to Organization Safety
        </Typography>
        <Typography variant="h6" fontSize={22} textAlign="center">
          Equip your organization with the knowledge and confidence to respond
          to high-stress situations, whether that means preventing escalation,
          or surviving an active threat. Developed by law enforcement and
          instructional design professionals, S3G&apos;s self-paced training
          subscription includes{courseInfo ? ` ${courseInfo.length} ` : ' '}
          essential online programs.
        </Typography>
        {courseInfo ? (
          <Box>
            <Grid container spacing={4} marginTop="2em" marginBottom="4em">
              {courseInfo.map((course) => (
                <Grid item sm={12} md={4} key={course.slug}>
                  <ReactCardFlip
                    isFlipped={isFlippedMap[course.slug]}
                    flipDirection="vertical"
                  >
                    {/* Front of card */}
                    <Card elevation={cardElevation}>
                      <CardActionArea
                        onClick={() => flipCard(course.slug)}
                        sx={{
                          height: cardHeight,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="250px"
                          image={cardMapping[course.slug]}
                          alt={course.title}
                        />
                        <CardContent
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="h5"
                            component="div"
                            textAlign="center"
                            fontWeight="600"
                          >
                            {course.title}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            textAlign="center"
                            fontWeight={500}
                          >
                            {course.subtitle}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                    {/* Back of card */}
                    <Card elevation={cardElevation}>
                      <CardActionArea
                        onClick={() => flipCard(course.slug)}
                        sx={{
                          height: cardHeight,
                        }}
                      >
                        <CardContent
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '1em',
                          }}
                        >
                          {getIconForCourse(course.slug)}
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            fontSize="1.2em"
                            textAlign="center"
                          >
                            {course.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </ReactCardFlip>
                </Grid>
              ))}
            </Grid>
            <Typography variant="h6" textAlign="center" fontSize={22}>
              These programs deliver real-world preparedness with
              trauma-informed methods, short video modules, and clear learning
              outcomes.
            </Typography>
          </Box>
        ) : (
          // The course info did not successfully load. Don't show this section
          <></>
        )}
      </Container>
    </Box>
  );
};

export default WhatWeDoSection;
